<template>
  <home-floating-window
    :title="$tc('Bundle', 1)"
    @close="closeBundleWindow"
    @changePosition="$emit('changeBundleWindowPosition', $event)"
    :pos-x="position.x"
    :pos-y="position.y"
    :draggable="!loading && !dragIsDisabled"
    :height="165"
  >
    <template v-slot:iconWindows>
      <BundleSVG class="floating-container-svg" />
    </template>

    <template v-slot:objectDivWindows>
      <div class="floating-tool-content">
        <div class="bundle-objects-div scroll-fire-fox">
          <HighlightedBox
            :bad-object="badObject"
            :is-visible="showInstruction"
            :message="currentInstruction"
          />

          <div style="text-align: right; margin: 0">
            <v-checkbox
              v-model="pathwaysWithIgnore"
              :label="$i18n.t('DontUsePathwaysIgnored')"
              color="accent"
              class="multi-selection-checkbox"
              :ripple="false"
            />

            <v-btn
              v-if="bundleObjectsList.length > 0"
              small
              text
              class="clear-button"
              @click="clearBundle"
            >
              <v-icon style="padding-right: 7px">clear_all</v-icon>
              {{ $t("ClearAll") }}
            </v-btn>
          </div>

          <div
            class="floating-objects-wrapper"
            :class="{ 'bundle-direction-line': lineStringWKT.length > 1 }"
            style="margin-bottom: 5px"
          >
            <CableSouthSVG
              v-show="lineStringWKT.length > 1"
              style="position: absolute; left: -6px; bottom: -4px;"
            />

            <div
              class="floating-object-selected"
              v-for="(object, index) in bundleObjectsList"
              :key="object.uuid"
            >
              <TobyBoxSVG
                v-if="object.type.toLowerCase() === 'toby box'"
                class="floating-img"
              />
              <img
                v-else-if="object.type.toLowerCase() === 'cabinet'"
                class="floating-img"
                :src="
                  require(`@/assets/objectIcons/${object.type.toLowerCase() + '_t4'}.png`)
                "
              />
              <VirtualSVG
                v-else-if="object.type.toLowerCase() === 'virtual'"
                class="floating-img"
              />
              <img
                v-else
                class="floating-img"
                :src="
                  require(`@/assets/objectIcons/${object.type.toLowerCase()}.png`)
                "
              />

              <div
                class="floating-between-objects"
                v-if="object.uuid !== bundleObjectsList[0].uuid"
                :style="{ 'border-color': '#FF00FF' }"
              />

              <v-tooltip top style="display: block">
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" v-if="lineStringWKT[index].split" style="left: -3px;">alt_route</v-icon>
                </template>
                <span>{{ $t('BundleSplitTooltip') }}</span>
              </v-tooltip>

              <span style="text-align: left; padding-right: 5px">
                {{ objectIdentification(object) }}
              </span>

              <div
                class="floating-hover-options"
                @click="openBundleObjectMenu($event, object)"
              >
                <v-icon>more_vert</v-icon>
              </div>
            </div>
          </div>
        </div>
      </div>

      <home-window-button
        :label="bundleLabelButton"
        :disabled="!bundle.creatable"
        @buttonClick="createBundle"
      />

      <loading-spinner
        :active.sync="loading"
        :is-full-page="true"
        color="accent"
      />

      <window-object-menu
        v-if="bundleObjectMenu.open"
        :event="bundleObjectMenu.event"
        :object="bundleObjectMenu.object"
        :actionFrom="selectedToolObjectLayer"
        @updateGeoServer="updateGeoServer"
        @closeMenu="closeBundleObjectMenu"
        @openForm="openFormFromBundleObjectMenu"
        @openDetailForm="openFormFromBundleObjectMenu('detail')"
        @removeFromCable="removeObjectFromBundle"
        @splitBundle="splitBundle"
      />
    </template>
  </home-floating-window>
</template>

<script>
import { bus } from "@/main";
import { mapActions } from "vuex";
import { mixin as clickaway } from "vue-clickaway";
import BundleSVG from "@/assets/bundle_svgs/bundle.svg";
import HomeWindowButton from "../../HomeWindowButton";
import HomeFloatingWindow from '../HomeFloatingWindow';
import { Stroke } from "ol/style";
import { GeoJSON } from "ol/format";
import { Style } from "ol/style.js";
import HighlightedBox from "@/components/_Molecules/HighlightedBox";
import { listsMixin } from "@/mixins/listsMixin";
import LoadingSpinner from "vue-loading-overlay";
import CableSouthSVG from "@/assets/cable_menu/cable_south.svg";
import TobyBoxSVG from "@/assets/objectIcons/tobybox.svg";
import WindowObjectMenu from "../../ObjectMenu/WindowObjectMenu";
import VirtualSVG from "@/assets/objectIcons/virtual.svg";

export default {
  name: "BundleWindow",

  components: {
    HomeFloatingWindow,
    BundleSVG,
    HighlightedBox,
    LoadingSpinner,
    HomeWindowButton,
    CableSouthSVG,
    TobyBoxSVG,
    WindowObjectMenu,
    VirtualSVG
  },

  mixins: [clickaway, listsMixin],

  props: {
    bundle: {
      type: Object
    },
    lineStringWKT: {
      type: Array
    },
    vectorSource: {},
    position: Object,
  },

  data() {
    return {
      dragIsDisabled: false,
      loading: false,
      badObject: false,
      bundleObjectsList: [],
      temporaryBundles: [],
      bundleObjectMenu: {
        open: false,
        event: null,
        object: null
      },
      pathwaysWithIgnore: false
    }
  },

  computed: {
    /**
     * Indicates if the instruction is shown or not
     */
    showInstruction() {
      return this.currentInstruction !== "";
    },

    /**
     * Returns the instruction for the creation
     */
    currentInstruction() {
      return this.$t("BundleInstruction");
    },

    /**
     * Returns the label of the form button
     */
    bundleLabelButton() {
      return this.$t("Create");
    },

    objectIdentifiers() {
      return this.$store.getters.selectedObjectIdentifiers;
    },

    selectedToolObjectLayer() {
      return this.$store.getters.tools_selectedObjectLayer;
    },
  },

  /**
   * Listens for events to close the window
   */
  mounted() {
    bus.$on("escape-bundle-window", () => this.closeBundleWindow());
    bus.$on("deactivate-bundle_creation", () => this.closeBundleWindow());
    bus.$on("highlight-bundle-box", () => this.highlightBundleBox());
    bus.$on("add-node-to-bundle", () => this.addNode());
  },

  /**
   * Stops listening for the events
   */
  destroyed() {
    bus.$off("escape-bundle-window");
    bus.$off("deactivate-bundle_creation");
    bus.$off("highlight-bundle-box");
    bus.$off("add-node-to-bundle");
  },

  watch: {
    pathwaysWithIgnore() {
      this.findBestRoute();
    },
  },

  methods: {
    ...mapActions([
      "BEST_ROUTE",
      "fetchObjectByUUID",
      "UPDATE_SELECTED_OBJECT_IDENTIFIERS",
      "UPDATE_SELECTED_OBJECT_DATA"
    ]),

    closeBundleWindow() {
      this.$emit("close")
    },

    createBundle() {
      this.$emit('createBundle');
    },

    highlightBundleBox() {
      this.badObject = true;
      setTimeout(() => {
        this.badObject = false;
      }, 550);
    },

    addNode() {
      let wkt = "";
      this.$emit("add-node", this.objectIdentifiers.coordinates, result => {
        wkt = result;
      });
      let object = {
        type: this.objectIdentifiers.objectType,
        uuid: this.objectIdentifiers.uuid,
        text_geom: wkt,
        layerName: this.objectIdentifiers.layerName,
        split: false
      };

      if (
        !this.lineStringWKT.find(
          point => point.uuid === this.objectIdentifiers.uuid
        )
      ) {
        if (
          object.type === "technical_local_olt" ||
          object.type === "technical_local_fdp"
        ) {
          object.type = "technical_local";
        }

        this.lineStringWKT.push(object);

        this.bundleObjectsList.push(this.objectIdentifiers);
        this.scrollBundleWindow();
        this.findBestRoute();
      }
    },

    scrollBundleWindow() {
      this.$nextTick(() => {
        let container = this.$el.querySelector(".bundle-objects-div");
        container.scrollTop = container.scrollHeight;
      });
    },

    findBestRoute() {
      if (this.bundleObjectsList.length < 1) {
        return;
      }
      if (this.bundleObjectsList.length === 1) {
        this.vectorSource.clear();
        return;
      }
      if (this.lineStringWKT.length > 1) {
        this.BEST_ROUTE({
          node: this.lineStringWKT,
          projection: 3857,
          filterPathwaysWithIgnore: this.pathwaysWithIgnore
        })
          .then(resp => {
            this.vectorSource.clear();

            let pathways = resp.data.data.pathways;
            this.bundle.pathways = pathways;

            if (Array.isArray(pathways)) {
              for (const pathway of pathways) {
                let features = new GeoJSON().readFeatures(pathway.pw_geo_json, {
                  featureProjection: "EPSG:3857"
                });

                let feature = features[0];
                let style = this.defineBundleStyle();
                feature.setStyle(style);
                this.temporaryBundles.push(feature);
                this.vectorSource.addFeature(feature);
              }

              this.updateGeoServer();
            }

            this.bundle.creatable = this.lineStringWKT.length > 1;
          })
          .catch(error => {
            this.lineStringWKT.pop();
            this.bundleObjectsList.pop();
            this.$toasted.global.toast_error(error.response.data.data);
          });
      }
    },

    defineBundleStyle() {
      return new Style({
        stroke: new Stroke({ color: "#FF00FF", width: 3 })
      });
    },

    updateGeoServer() {
      this.$emit("updateGeoserver");
    },

    clearBundle() {
      this.bundle.creatable = false;
      this.bundleObjectsList = [];
      this.$emit("clear");
    },

    objectIdentification(object) {
      let label = "";
      if (object.label) {
        label += object.label;
      } else {
        label += object.id;
      }
      return label;
    },

    openBundleObjectMenu(ev, object) {
      let objectIndex = this.bundleObjectsList.findIndex(bundleObject => bundleObject.uuid === object.uuid);
      let objectSplitValue = objectIndex > -1 && this.lineStringWKT[objectIndex] ? this.lineStringWKT[objectIndex].split : false;
      setTimeout(() => {
        if (this.bundleObjectMenu.open) {
          this.bundleObjectMenu.event = null;
          this.bundleObjectMenu.object = null;
        } else {
          this.bundleObjectMenu.event = ev;
          this.bundleObjectMenu.object = object;
          this.bundleObjectMenu.object.split = objectSplitValue;
        }
        this.bundleObjectMenu.open = !this.bundleObjectMenu.open;
      }, 0);
    },

    closeBundleObjectMenu() {
      this.bundleObjectMenu.open = false;
    },

    openFormFromBundleObjectMenu(action) {
      this.closeBundleObjectMenu();
      this.getObjectDataFromBundleMenu(this.bundleObjectMenu.object, action);
    },

    getObjectDataFromBundleMenu(objectIdentifiers, formType) {
      let endpoint = objectIdentifiers.endpoint;
      let uuid = objectIdentifiers.uuid;
      let fields = this.getFields(endpoint);
      let layerName = objectIdentifiers.layerName;

      this.fetchObjectByUUID({
        endpoint: endpoint,
        objectUUID: uuid,
        fieldsToInclude: fields
      })
        .then(resp => {
          this.UPDATE_SELECTED_OBJECT_IDENTIFIERS(objectIdentifiers);
          this.UPDATE_SELECTED_OBJECT_DATA(resp.data.data[0]);
          if (formType === "detail") {
            this.$emit("openForm", "Detail", "edit");
          } else {
            this.$emit("openForm", layerName, formType);
          }
        })
        .catch(error => {
          this.$toasted.global.toast_error(error.response.data.data);
          console.log(error);
        });
    },

    getFields(layer) {
      switch (layer) {
        case "technicalpoints":
          return "images";
        case "buildings":
          return "images,technicalsite,technicallocals";
        case "technicallocals":
          return "technicalsite";
        default:
          return "";
      }
    },

    removeObjectFromBundle(uuid) {
      this.closeBundleObjectMenu();
      let index = this.bundleObjectsList.findIndex(point => point.uuid === uuid);
      if (index > -1) {
        this.lineStringWKT.splice(index, 1);
        this.bundleObjectsList.splice(index, 1);
        this.findBestRoute();
      }
      this.bundle.creatable = this.lineStringWKT.length > 1;
    },

    splitBundle(uuid) {
      this.closeBundleObjectMenu();
      let index = this.bundleObjectsList.findIndex(point => point.uuid === uuid);
      if (index > -1) {
        this.lineStringWKT[index].split = !this.lineStringWKT[index].split
      }
    }
  }
}
</script>

<style scoped>
input:invalid,
textarea:invalid {
  border: 1px solid var(--red);
}

.bundle-objects-div {
  padding: 5px 10px;
}

.vue-treeselect {
  margin-bottom: 5px;
}

.bundle-objects-div > .select-object {
  margin-top: 5px;
  margin-bottom: 10px;
}

.clear-button {
  margin: 0 0 5px;
  color: #515358;
  font-size: 12px;
  text-transform: none;
}

.bundle-direction-line {
  border-left: 1px dashed var(--primary-highlight);
}

.floating-tool-content {
  padding: 0 20px 5px;
  min-height: 70px;
}
</style>


// WEBPACK FOOTER //
// src/components/Home/Content/ObjectWindow/Bundle/BundleWindow.vue